import { Component, Prop, Vue } from "vue-property-decorator";

import { CreateElement } from "vue";
import Card from "@/components/Card.tsx";
import AudioHandler, { RECORDING_STATE } from "./Recorder";
import { Module, TTMJ } from "@/store/enums.ts";
import NotificationBar from "@/components/Notification";
import {
  submitFiles,
  fetchAllItemsURL,
  fetchFileURLByName
} from "./StorageHandler";
import firebase from "firebase/app";
import { taskCompleted } from "./utils";

enum ELLISION_STATE {
  DESCRIPTION,
  EXAMPLE,
  LIST
}

@Component
export default class TalkToMeJrEllision extends Vue {
  private _frame: ELLISION_STATE;
  private _onItem: number = NaN;
  private _data: {
    questionWithoutSound: string;
    questionWithSound: string;
    answerWithSound?: string;
    answerWithoutSound?: string;
  }[];
  private _withSound: boolean = true;
  private _doneList: boolean = false;
  private _disableRecording: boolean = true;
  private _enableNext: boolean = false;
  private _timeout: number = 3000;
  private _withoutSoundBlob: any;
  private _withSoundBlob: any;
  private _exampleURL: string[] = [];
  private _enableDescribeNext!: boolean;
  private audioLoc =
    "/res/protected/t2mejr_instructions/ttmj_ellision_autoplay.mp3";
  private supposedCurrentTime: any;

  recordStoppedClicked(audio: any, duration: number, blob?: any) {
    this._disableRecording = true;
    this._enableNext = true;
    if (!this._withSound) this._withoutSoundBlob = blob;
    else this._withSoundBlob = blob;
    this.$forceUpdate();
  }

  async changeRecordingToNext() {
    if (this._withSound) {
      this._withSound = false;
      this.$forceUpdate();
      let a: any = this.$refs.audio;
      a.src = this._data[this._onItem].questionWithoutSound;
      this._enableNext = false;
      a.load();
      return;
    }
    this._withSound = true;
    this._data[this._onItem].answerWithSound = this._withSoundBlob;
    this._data[this._onItem].answerWithoutSound = this._withoutSoundBlob;

    this._enableNext = false;
    if (this._onItem + 1 === this._data.length) {
      this._doneList = true;
      this._enableNext = true;
      await this._submitData();
      this.$router.push({ name: "t2mejr-tasklist" });
      this.$forceUpdate();
    } else {
      ++this._onItem;
    }
    this.$forceUpdate();
    let a: any = this.$refs.audio;
    a.src = this._data[this._onItem].questionWithSound;
    a.load();
  }

  constructor() {
    super();
    this._frame = ELLISION_STATE.DESCRIPTION;
    this._data = [];
  }

  created() {
    this._enableDescribeNext = false;
    this.$store.dispatch("setClip", {
      path: this.audioLoc,
      callbackFunction: () => {
        this._enableDescribeNext = true;
        this.$forceUpdate();
      }
    });
    this.$forceUpdate();

    this._getData();
    this._frame = ELLISION_STATE.DESCRIPTION;
    this._disableRecording = true;
    this._withSound = true;
  }

  beforeDestroy() {
    this.$store.dispatch("removeClip", this.audioLoc);
  }

  private async _submitData() {
    let currentUser = firebase.auth().currentUser;

    if (!currentUser) return;

    let dir: string = "users/" + currentUser.uid + "/t2mejr/ellision/";
    let i = 0;
    this.$store.dispatch("addNotification", {
      type: "info",
      value: "Uploading your answers please wait",
      timeout: 5000,
      icon: "mdi-account"
    });

    let allPromisesResolve = true;
    for (const item of this._data) {
      await Promise.all([
        submitFiles(
          dir + "with_sound/ellision_" + i + ".webm",
          item.answerWithSound
        ),
        submitFiles(
          dir + "without_sound/ellision_" + ++i + ".webm",
          item.answerWithoutSound
        )
      ]).catch(error => {
        // alert(JSON.stringify(error));
        allPromisesResolve = false;
      });

      if (!allPromisesResolve) break;
    }
    if (allPromisesResolve && currentUser) {
      this.$store.dispatch("addNotification", {
        type: "success",
        value: "Successfully Uploaded!",
        timeout: 5000,
        icon: "mdi-account"
      });
      taskCompleted(currentUser.uid, TTMJ.ELISON);
    } else {
      this.$store.dispatch("addNotification", {
        type: "error",
        value: "Upload has failed. Please try again!",
        timeout: 5000,
        icon: "mdi-account"
      });
    }
  }

  private _enableRecording() {
    (this.$refs.audio as any).src = "";
    this._disableRecording = false;
    this.$forceUpdate();
  }

  updated() {
    let a: any = this.$refs.audio;

    if (a) {
      a.addEventListener("ended", this._enableRecording);
      a.addEventListener("timeupdate", () => {
        if (!a.seeking) {
          this.supposedCurrentTime = a.currentTime;
        }
      });
      // prevent user from seeking
      a.addEventListener("seeking", () => {
        // guard agains infinite recursion:
        // user seeks, seeking is fired, currentTime is modified, seeking is fired, current time is modified, ....
        var delta = a.currentTime - this.supposedCurrentTime;
        if (Math.abs(delta) > 0.01) {
          a.currentTime = this.supposedCurrentTime;
        }
      });
    }
  }

  private async _getData() {
    this._onItem = 0;
    this._data = [];
    let data: any = await fetchAllItemsURL("res/protected/ellision");
    this._exampleURL = data;

    data = await fetchAllItemsURL("res/protected/ellision/without_sound");
    data.map((i: any) =>
      this._data.push({ questionWithoutSound: i, questionWithSound: "" })
    );

    data = await fetchAllItemsURL("res/protected/ellision/with_sound");

    data.map((i: string, idx: number) => {
      this._data[idx].questionWithSound = i;
    });
  }

  render(h: CreateElement) {
    return (
      <span>
        {this._description()}
        {this._example()}
        {this._words()}
      </span>
    );
  }

  private _example() {
    return (
      this._frame === ELLISION_STATE.EXAMPLE && (
        <Card title={`Listen to the two examples.`} mod={Module.TTMJ}>
          <v-container slot="mainContent">
            <v-row>
              <p>Here is one example: </p>
            </v-row>
            <v-row justify="center">
              <audio autoplay controls controlsList="nodownload">
                <source src={this._exampleURL[0]} type="audio/mp3" />
              </audio>
            </v-row>
            <v-row>
              <p>Let's listen to another example: </p>
            </v-row>
            <v-row justify="center">
              <audio controls controlsList="nodownload">
                <source src={this._exampleURL[1]} type="audio/mp3" />
              </audio>
            </v-row>
          </v-container>
          <div slot="actions">
            <v-btn
              onClick={() => {
                this._frame = ELLISION_STATE.LIST;
                this.$forceUpdate();
              }}
              color="warning"
            >
              Next
            </v-btn>
          </div>
        </Card>
      )
    );
  }

  private _words() {
    const audio = (src: string) => {
      return (
        <audio autoplay controls ref="audio" controlsList="nodownload">
          <source src={src} type="audio/mp3" />
        </audio>
      );
    };
    const content = () => {
      return (
        <v-container>
          <p>Follow the audio instructions.</p>
          <v-row justify="center">
            {!this._withSound
              ? audio(this._data[this._onItem].questionWithoutSound)
              : audio(this._data[this._onItem].questionWithSound)}
          </v-row>
        </v-container>
      );
    };
    return (
      this._frame === ELLISION_STATE.LIST && (
        <Card
          title={`Follow the audio instructions (Part ${this._onItem + 1}/${
            this._data.length
          }).`}
          mod={Module.TTMJ}
        >
          <div slot="mainContent">{content()}</div>
          <div slot="actions">
            <v-row>
              <v-col cols={16}>
                <AudioHandler
                  recordID="Ellision"
                  label={{
                    start: "Record",
                    end: "Stop Recording"
                  }}
                  recordingStartClick={() => {
                    // this._disablePlayback = true;
                  }}
                  disableUserControl
                  disabled={this._doneList || this._disableRecording}
                  callbackOnStop={this.recordStoppedClicked}
                />
              </v-col>
            </v-row>
            <v-row class="mx-auto text-center">
              <v-col>
                <v-btn
                  disabled={!this._enableNext}
                  onclick={this.changeRecordingToNext}
                >
                  Next
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </Card>
      )
    );
  }

  private _description() {
    return (
      this._frame === ELLISION_STATE.DESCRIPTION && (
        <Card title="I can say words without some sounds." mod={Module.TTMJ}>
          <div slot="mainContent">
            <p>
              A word will be said out loud to you. Please say it out loud. Then,
              you will be asked to remove a sound from the word. Say it again
              with that sound missing. Let's start with some examples.
            </p>

            <p>Are you ready? Click “Next” to get started with the examples.</p>
          </div>
          <div slot="actions">
            <v-btn
              disabled={!this._enableDescribeNext}
              onClick={() => {
                this._frame = ELLISION_STATE.EXAMPLE;
                this.$forceUpdate();
              }}
              color="warning"
            >
              Next
            </v-btn>
          </div>
        </Card>
      )
    );
  }
}

const component = TalkToMeJrEllision;
